import React, { useState, useEffect, Suspense, lazy } from 'react';
import { API_BASE } from './api';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';

import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Avatar,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Email, Lock, Person } from '@mui/icons-material';
import DashboardHomeSimple from './pages/DashboardHomeSimple';
import Contratos from './pages/Contratos';
import Clientes from './pages/Clientes';
import FinanceiroPage from './pages/FinanceiroPage';
import TestesLojaMenu from './pages/TestesLojaMenu';
import TestesLoja from './pages/TestesLoja';
import TestesArCondicionado from './pages/TestesArCondicionado';
import TestesElevadores from './pages/TestesElevadores';
import TestesChecklistNovo from './pages/TestesChecklistNovo';
import TestesChecklistConsulta from './pages/TestesChecklistConsulta';
import Despesas from './pages/Despesas';
import Fornecedores from './pages/Fornecedores';
import ResumoMensal from './pages/ResumoMensal';
import ValorMateriais from './pages/ValorMateriais';
import Reciclagem from './pages/Reciclagem';
import ProcessosFinanceiros from './pages/ProcessosFinanceiros';
import CadastroUsuarios from './pages/CadastroUsuarios';
import RelatorioObras from './pages/RelatorioObras';
import ProcessoCadastroEmpreiteira from './pages/ProcessoCadastroEmpreiteira';
import CadastroEmpreiteiraPublica from './pages/CadastroEmpreiteiraPublica';
import CadastroEmpreiteiraDocumentoExterno from './pages/CadastroEmpreiteiraDocumentoExterno';
import Equipamentos from './pages/Equipamentos';
import Locacoes from './pages/Locacoes';

// Lazy-load do módulo Agente (~221KB) para reduzir o bundle inicial
const Agente = lazy(() => import('./pages/Agente'));

// Componente Login inline para evitar problemas de import
function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pwaDialogOpen, setPwaDialogOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [osType, setOsType] = useState('other');

  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    // Detect OS para fallback
    const userAgent = window.navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setOsType('ios');
    } else if (/android/.test(userAgent)) {
      setOsType('android');
    }

    const handler = (e) => {
      // Impede o Chrome de exibir o prompt padrão
      e.preventDefault();
      // Salva o evento para disparar mais tarde
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Mostra o prompt nativo (Android/Chrome/Edge)
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        console.log('App instalado');
      }
      setDeferredPrompt(null);
    } else {
      // Não tem suporte nativo para chamar o botão (ex: iOS ou manifest ausente)
      // Mostra o nosso Modal com instruções específicas
      setPwaDialogOpen(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) throw new Error('Usuário ou senha inválidos');
      const data = await res.json();
      login(data.access_token, data.user);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{ p: 4, borderRadius: 4, background: 'rgba(255, 255, 255, 0.95)' }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Avatar
              sx={{
                width: 60,
                height: 60,
                mx: 'auto',
                mb: 2,
                background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
              }}
            >
              <Person sx={{ fontSize: 28, color: 'white' }} />
            </Avatar>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1f2937' }}>
              Login
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              placeholder="usuário@empresa.com"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#64748b' }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              placeholder="senha"
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#64748b' }} />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !username || !password}
              sx={{ py: 1.8, borderRadius: 3, fontSize: '1rem', fontWeight: 600 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              onClick={handleInstallClick}
              sx={{
                mt: 2,
                py: 1.5,
                borderRadius: 3,
                fontSize: '0.95rem',
                fontWeight: 600,
                color: '#4b5563',
                borderColor: '#cbd5e1',
                backgroundColor: 'white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#f8fafc',
                  borderColor: '#94a3b8',
                },
              }}
            >
              Como adicionar à tela inicial?
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* Modal de instruções para adicionar à tela inicial */}
      <Dialog
        open={pwaDialogOpen}
        onClose={() => setPwaDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1, textAlign: 'center' }}>
          Adicionar à Tela Inicial
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          {osType === 'ios' && (
            <>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#3b82f6' }}>
                Você está no iPhone / iPad:
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: '#4b5563' }}>
                1. Toque no ícone Compartilhar (quadrado com uma seta) na barra inferior.
                <br />
                <br />
                2. Role o menu para baixo e selecione
                <b>"Adicionar à Tela de Início"</b>
                (icone de mais ➕).
              </Typography>
            </>
          )}

          {osType === 'android' && (
            <>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#3b82f6' }}>
                Você está no Android:
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: '#4b5563' }}>
                Parece que seu navegador não suporta a instalação automática.
                <br />
                <br />
                1. Toque no ícone de opções (três pontos ⋮) no canto superior direito.
                <br />
                <br />
                2. Selecione <b>"Adicionar à tela inicial"</b> ou <b>"Instalar aplicativo"</b>.
              </Typography>
            </>
          )}

          {osType === 'other' && (
            <>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#3b82f6' }}>
                Instruções Gerais:
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, mb: 2, color: '#4b5563' }}>
                <b>Android (Chrome):</b>
                <br />
                No menu (⋮), escolha "Instalar aplicativo" ou "Adicionar à tela inicial".
              </Typography>
              <Typography variant="body2" sx={{ color: '#4b5563' }}>
                <b>iOS (Safari):</b>
                <br />
                Toque em Compartilhar e selecione "Adicionar à Tela de Início" (➕).
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button
            onClick={() => setPwaDialogOpen(false)}
            variant="contained"
            fullWidth
            sx={{ borderRadius: 2, py: 1 }}
          >
            Entendi
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const theme = createTheme({
  palette: {
    primary: { main: '#3b82f6' },
    secondary: { main: '#f59e0b' },
  },
});

const LoginRoute = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div style={{ padding: 24 }}>Carregando…</div>;
  if (isAuthenticated) return <Navigate to="/" replace />;

  return <Login />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <Router>
          <ErrorBoundary>
            <Routes>
              <Route
                path="/cadastro-empreiteira-publico"
                element={<CadastroEmpreiteiraPublica />}
              />
              <Route
                path="/cadastro-empreiteira-documento/:accessToken"
                element={<CadastroEmpreiteiraDocumentoExterno />}
              />
              <Route path="/login" element={<LoginRoute />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <DashboardHomeSimple />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/estrutura/cadastro-empreiteira"
                element={
                  <ProtectedRoute page="/estrutura">
                    <ProcessoCadastroEmpreiteira />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/contratos"
                element={
                  <ProtectedRoute page="/contratos">
                    <Contratos />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clientes"
                element={
                  <ProtectedRoute page="/clientes">
                    <Clientes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/despesas"
                element={
                  <ProtectedRoute page="/despesas">
                    <Despesas />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fornecedores"
                element={
                  <ProtectedRoute page="/fornecedores">
                    <Fornecedores />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/resumo-mensal"
                element={
                  <ProtectedRoute page="/resumo-mensal">
                    <ResumoMensal />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/valor-materiais"
                element={
                  <ProtectedRoute page="/valor-materiais">
                    <ValorMateriais />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reciclagem"
                element={
                  <ProtectedRoute page="/financeiro">
                    <Reciclagem />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/processos"
                element={
                  <ProtectedRoute page="/financeiro">
                    <ProcessosFinanceiros />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cadastro-usuarios"
                element={
                  <ProtectedRoute page="/cadastro-usuarios">
                    <CadastroUsuarios />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relatorio-obras"
                element={
                  <ProtectedRoute page="/relatorio-obras">
                    <RelatorioObras />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agente"
                element={
                  <ProtectedRoute page="/agente">
                    <Suspense
                      fallback={
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '60vh',
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      }
                    >
                      <Agente />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro"
                element={
                  <ProtectedRoute page="/financeiro">
                    <FinanceiroPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/testes-loja-menu"
                element={
                  <ProtectedRoute page="/testes-loja-menu">
                    <TestesLojaMenu />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/testes-loja"
                element={
                  <ProtectedRoute page="/testes-loja" requiredPermission="update">
                    <TestesLoja />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/testes-ar-condicionado"
                element={
                  <ProtectedRoute page="/testes-ar-condicionado" requiredPermission="update">
                    <TestesArCondicionado />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/testes-elevador"
                element={
                  <ProtectedRoute page="/testes-elevador">
                    <TestesElevadores />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/testes-checklist/novo"
                element={
                  <ProtectedRoute page="/testes-loja-menu">
                    <TestesChecklistNovo />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/testes-checklist/consulta"
                element={
                  <ProtectedRoute page="/testes-loja-menu">
                    <TestesChecklistConsulta />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/equipamentos"
                element={
                  <ProtectedRoute page="/equipamentos">
                    <Equipamentos />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pta-controle"
                element={
                  <ProtectedRoute page="/pta-controle">
                    <Locacoes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/locacoes"
                element={
                  <ProtectedRoute page="/locacoes">
                    <Locacoes />
                  </ProtectedRoute>
                }
              />
              {/* Rota de cadastro-usuarios removida; cadastro agora é feito via PHP */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </ErrorBoundary>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
